import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';
import { Link } from 'react-router-dom';

const safetyFeatures = [
    {
        icon: '🔒',
        title: 'Minimum Age Policy',
        description: 'Swag Shots is intended for users aged 13 and above, with additional review for youth safety concerns.',
    },
    {
        icon: '🛡️',
        title: 'Content Moderation',
        description: 'Automated safety checks and human review processes help identify and act on harmful material.',
    },
    {
        icon: '👥',
        title: 'Guardian Support',
        description: 'Parents and guardians can contact us for safety concerns, account questions, or content reports.',
    },
    {
        icon: '🚨',
        title: 'Quick Reporting',
        description: 'One-tap reporting for any concerning content or behavior with priority handling.',
    },
    {
        icon: '🎓',
        title: 'Digital Literacy',
        description: 'Educational resources about digital citizenship, privacy awareness, and critical thinking.',
    },
    {
        icon: '⏰',
        title: 'Healthy Use',
        description: 'We encourage age-appropriate use, privacy awareness, and responsible participation in the community.',
    },
    {
        icon: '🔐',
        title: 'Privacy Choices',
        description: 'Users can manage profile visibility, interactions, and account settings inside the app.',
    },
    {
        icon: '💬',
        title: 'Interaction Controls',
        description: 'Users can block abusive users and report concerning behavior from app interaction screens.',
    },
    {
        icon: '🧠',
        title: 'Smart Content Filtering',
        description: 'Safety filters and moderation review help reduce exposure to objectionable or age-inappropriate material.',
    },
];

const prohibitedBehaviors = [
    {
        icon: '🚫',
        title: 'Grooming & Predatory Behavior',
        description: 'Any form of grooming or predatory behavior targeting minors',
        action: 'Immediate ban + legal action'
    },
    {
        icon: '⚠️',
        title: 'Sexual Exploitation',
        description: 'Sexual exploitation or abuse of children',
        action: 'Immediate ban + authority report where required'
    },
    {
        icon: '🔞',
        title: 'CSAM Content',
        description: 'Sharing, requesting, or facilitating child sexual abuse material',
        action: 'Immediate ban + law enforcement'
    },
    {
        icon: '😢',
        title: 'Bullying & Harassment',
        description: 'Bullying, harassment, or intimidation of minors',
        action: 'Content removal + account suspension'
    },
    {
        icon: '🆘',
        title: 'Self-Harm Encouragement',
        description: 'Encouraging self-harm or dangerous challenges',
        action: 'Content removal + mental health resources'
    },
];

const educationalResources = [
    {
        icon: '👨‍👩‍👧‍👦',
        title: 'Digital Citizenship',
        description: 'Teaching responsible online behavior and digital ethics'
    },
    {
        icon: '🔒',
        title: 'Privacy Awareness',
        description: 'Understanding personal information protection and safe sharing'
    },
    {
        icon: '🛡️',
        title: 'Cyberbullying Prevention',
        description: 'Recognizing and responding to online harassment'
    },
    {
        icon: '🧠',
        title: 'Critical Thinking',
        description: 'Evaluating online information and avoiding misinformation'
    },
    {
        icon: '⚖️',
        title: 'Healthy Usage',
        description: 'Maintaining balanced screen time and digital wellness'
    },
    {
        icon: '🤝',
        title: 'Law Enforcement Cooperation',
        description: 'Working with appropriate authorities and child protection organizations where required'
    },
];

const reportingSteps = [
    {
        step: '1',
        title: 'Identify the Issue',
        description: 'Document what happened, when it occurred, and who was involved.',
    },
    {
        step: '2',
        title: 'Use In-App Reporting',
        description: 'Tap the report button on any content, profile, or message to flag it immediately.',
    },
    {
        step: '3',
        title: 'Contact Support',
        description: 'For serious safety concerns, email info@swagshots.com with detailed information.',
    },
    {
        step: '4',
        title: 'Follow Up',
        description: 'We will investigate and respond to all reports within 24 hours for safety issues.',
    },
];

export const ChildSafetyPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Child Safety - Swag Shots"
                description="Swag Shots Child Safety Policy - Our commitment to protecting children and creating a safe environment for young users."
                url="https://www.swagshots.com/child-safety"
            />

            <div className="pt-24 pb-16">
                <div className="container-custom">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Child Safety</h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our commitment to protecting children and creating a safe environment for young users
                        </p>
                    </div>

                    {/* Safety Commitment */}
                    <div className="bg-gradient-to-br from-primary-600 to-primary-500 text-white rounded-xl p-8 mb-16 text-center">
                        <h2 className="text-3xl font-bold mb-4">Our Safety Commitment</h2>
                        <p className="text-primary-100 mb-6 max-w-3xl mx-auto text-lg">
                            At Swag Shots, the safety and well-being of our young users is our top priority. We've implemented
                            comprehensive measures to create a secure, age-appropriate environment where children can express
                            themselves safely.
                        </p>
                        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 max-w-2xl mx-auto">
                            <p className="text-white font-semibold">
                                Minimum Age: 13 years old
                            </p>
                            <p className="text-primary-100 text-sm mt-2">
                                Enhanced protections for users under 18
                            </p>
                        </div>
                    </div>

                    {/* Safety Features */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Safety Features</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {safetyFeatures.map((feature, index) => (
                                <div
                                    key={index}
                                    className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                >
                                    <div className="text-5xl mb-6">{feature.icon}</div>
                                    <h3 className="text-xl font-semibold text-primary-600 mb-4">{feature.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Prohibited Behavior - Zero Tolerance */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                            🚫 Prohibited Behavior - Zero Tolerance
                        </h2>
                        <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-8">
                            <p className="text-center text-red-800 font-semibold mb-8 text-lg">
                                The following behaviors result in immediate account termination and law enforcement reporting:
                            </p>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {prohibitedBehaviors.map((behavior, index) => (
                                    <div
                                        key={index}
                                        className="bg-white border-2 border-red-300 rounded-xl p-6 text-center"
                                    >
                                        <div className="text-4xl mb-4">{behavior.icon}</div>
                                        <h3 className="text-lg font-semibold text-red-800 mb-3">{behavior.title}</h3>
                                        <p className="text-gray-700 mb-4 leading-relaxed">{behavior.description}</p>
                                        <div className="bg-red-100 rounded-lg p-3">
                                            <p className="text-red-800 text-sm font-semibold">{behavior.action}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Educational Resources */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Educational Resources</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {educationalResources.map((resource, index) => (
                                <div
                                    key={index}
                                    className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 text-center hover:shadow-lg transition-all duration-300"
                                >
                                    <div className="text-5xl mb-6">{resource.icon}</div>
                                    <h3 className="text-xl font-semibold text-blue-800 mb-4">{resource.title}</h3>
                                    <p className="text-gray-700 leading-relaxed">{resource.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* For Parents Section */}
                    <section className="max-w-6xl mx-auto mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">For Parents & Guardians</h2>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-10">
                            <div className="grid md:grid-cols-2 gap-12">
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                                        <span className="text-green-600 mr-3">✓</span>
                                        What You Can Do
                                    </h3>
                                    <ul className="space-y-4">
                                        <li className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                                            <span className="text-primary-600 mr-4 mt-1 text-lg">•</span>
                                            <span className="text-gray-700 leading-relaxed">Review your child's privacy settings together</span>
                                        </li>
                                        <li className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                                            <span className="text-primary-600 mr-4 mt-1 text-lg">•</span>
                                            <span className="text-gray-700 leading-relaxed">Discuss appropriate online behavior and digital citizenship</span>
                                        </li>
                                        <li className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                                            <span className="text-primary-600 mr-4 mt-1 text-lg">•</span>
                                            <span className="text-gray-700 leading-relaxed">Set healthy boundaries for app usage and online interactions</span>
                                        </li>
                                        <li className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                                            <span className="text-primary-600 mr-4 mt-1 text-lg">•</span>
                                            <span className="text-gray-700 leading-relaxed">Monitor your child's connections and interactions</span>
                                        </li>
                                        <li className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                                            <span className="text-primary-600 mr-4 mt-1 text-lg">•</span>
                                            <span className="text-gray-700 leading-relaxed">Report any concerning content or behavior immediately</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-6">
                                    <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                                        <span className="text-red-600 mr-3">⚠️</span>
                                        Red Flags to Watch For
                                    </h3>
                                    <ul className="space-y-4">
                                        <li className="flex items-start bg-red-50 border border-red-200 p-4 rounded-lg">
                                            <span className="text-red-600 mr-4 mt-1 text-lg">⚠️</span>
                                            <span className="text-gray-700 leading-relaxed">Requests for personal information</span>
                                        </li>
                                        <li className="flex items-start bg-red-50 border border-red-200 p-4 rounded-lg">
                                            <span className="text-red-600 mr-4 mt-1 text-lg">⚠️</span>
                                            <span className="text-gray-700 leading-relaxed">Attempts to move conversations off-platform</span>
                                        </li>
                                        <li className="flex items-start bg-red-50 border border-red-200 p-4 rounded-lg">
                                            <span className="text-red-600 mr-4 mt-1 text-lg">⚠️</span>
                                            <span className="text-gray-700 leading-relaxed">Inappropriate content or language</span>
                                        </li>
                                        <li className="flex items-start bg-red-50 border border-red-200 p-4 rounded-lg">
                                            <span className="text-red-600 mr-4 mt-1 text-lg">⚠️</span>
                                            <span className="text-gray-700 leading-relaxed">Cyberbullying or harassment</span>
                                        </li>
                                        <li className="flex items-start bg-red-50 border border-red-200 p-4 rounded-lg">
                                            <span className="text-red-600 mr-4 mt-1 text-lg">⚠️</span>
                                            <span className="text-gray-700 leading-relaxed">Secretive behavior about online activities</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Reporting Process */}
                    <section className="mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">How to Report Safety Concerns</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {reportingSteps.map((step, index) => (
                                <div key={index} className="text-center bg-white rounded-xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                                    <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-full w-16 h-16 flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg">
                                        {step.step}
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Age-Specific Protections */}
                    <section className="max-w-6xl mx-auto mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Age-Specific Protections</h2>
                        <div className="space-y-8">
                            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
                                <div className="flex items-center mb-6">
                                    <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold mr-4">
                                        Ages 13-15
                                    </div>
                                    <h3 className="text-2xl font-semibold text-primary-600">Enhanced Protection Mode</h3>
                                </div>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <span className="text-blue-700 font-medium">• Private accounts by default</span>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <span className="text-blue-700 font-medium">• Limited discovery features</span>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <span className="text-blue-700 font-medium">• Restricted direct messaging</span>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <span className="text-blue-700 font-medium">• Enhanced content filtering</span>
                                    </div>
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <span className="text-blue-700 font-medium">• No live streaming capabilities</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
                                <div className="flex items-center mb-6">
                                    <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold mr-4">
                                        Ages 16-17
                                    </div>
                                    <h3 className="text-2xl font-semibold text-primary-600">Graduated Access Mode</h3>
                                </div>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <span className="text-green-700 font-medium">• Gradual feature access with safety education</span>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <span className="text-green-700 font-medium">• Parental notification for monetization features</span>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <span className="text-green-700 font-medium">• Restricted interaction with adult users</span>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <span className="text-green-700 font-medium">• Enhanced privacy controls</span>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                        <span className="text-green-700 font-medium">• Content filtering with opt-out options</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Privacy & Data Protection for Minors */}
                    <section className="max-w-6xl mx-auto mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Privacy & Data Protection for Minors</h2>
                        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 border border-purple-200">
                            <p className="text-center text-purple-800 font-semibold mb-8 text-lg">
                                We comply with international child privacy laws and implement enhanced protections
                            </p>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h4 className="text-xl font-semibold text-purple-800 mb-4">Legal Compliance</h4>
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">🇺🇸</span>
                                            COPPA Compliance (Children's Online Privacy Protection Act - US)
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">🇪🇺</span>
                                            GDPR-K (General Data Protection Regulation for Kids - EU/UK)
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">🔒</span>
                                            Limited data collection for users under 18
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">👨‍👩‍👧</span>
                                            Parental consent mechanisms where legally mandated
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-4">
                                    <h4 className="text-xl font-semibold text-purple-800 mb-4">Data Protection Measures</h4>
                                    <ul className="space-y-3">
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">🗑️</span>
                                            Easy account and data deletion options for families
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">📊</span>
                                            Minimal data collection and processing
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">🔐</span>
                                            Enhanced encryption for minor accounts
                                        </li>
                                        <li className="text-gray-700 flex items-start">
                                            <span className="text-purple-600 mr-3 mt-1">⏱️</span>
                                            Shorter data retention periods
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Continuous Improvement */}
                    <section className="max-w-6xl mx-auto mb-20">
                        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Commitment to Continuous Improvement</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
                                <h3 className="text-2xl font-semibold text-primary-600 mb-6 flex items-center">
                                    <span className="text-3xl mr-3">🔄</span>
                                    Regular Updates
                                </h3>
                                <ul className="space-y-4">
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Quarterly policy reviews and safety measure updates
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Industry collaboration with safety organizations and experts
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Incorporating community feedback into safety improvements
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Ongoing investment in safety detection tools and technology
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
                                <h3 className="text-2xl font-semibold text-primary-600 mb-6 flex items-center">
                                    <span className="text-3xl mr-3">🤝</span>
                                    Partnership & Cooperation
                                </h3>
                                <ul className="space-y-4">
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Cooperation with law enforcement and safety organizations where required by law or appropriate for user safety
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Reporting suspected child exploitation to the appropriate authorities or safety organizations
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Cooperation with child protection agencies where applicable
                                    </li>
                                    <li className="text-gray-700 flex items-start">
                                        <span className="text-primary-600 mr-3 mt-1">•</span>
                                        Evidence preservation for legal proceedings
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-8 mt-8 text-center border border-green-200">
                            <h3 className="text-2xl font-bold text-green-800 mb-4">✅ Our Unwavering Commitment</h3>
                            <p className="text-green-700 leading-relaxed max-w-4xl mx-auto">
                                Swag Shots is dedicated to fostering a safe, respectful, and creative platform for everyone. We believe that with the right safeguards, technology can be a positive force in young people's lives. Violations of this policy may result in content removal, account suspension, or permanent bans.
                            </p>
                        </div>
                    </section>                    {/* Emergency Contact */}
                    <section className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-10 mb-20 text-center">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-6xl mb-6">🚨</div>
                            <h2 className="text-3xl font-bold text-red-800 mb-6">Emergency Safety Concerns</h2>
                            <p className="text-red-700 mb-8 text-lg leading-relaxed">
                                For immediate safety threats or illegal content involving minors:
                            </p>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="bg-white rounded-xl p-8 shadow-lg border border-red-200">
                                    <div className="text-4xl mb-4">📞</div>
                                    <h3 className="text-xl font-semibold text-red-800 mb-3">Contact Us Immediately</h3>
                                    <div className="space-y-2">
                                        <a
                                            href="tel:+919542006009"
                                            className="block text-red-700 font-semibold hover:text-red-800 transition-colors"
                                        >
                                            +91 95420-06009
                                        </a>
                                        <p className="text-red-600 text-sm">Safety contact</p>
                                    </div>
                                </div>
                                <div className="bg-white rounded-xl p-8 shadow-lg border border-red-200">
                                    <div className="text-4xl mb-4">✉️</div>
                                    <h3 className="text-xl font-semibold text-red-800 mb-3">Email Our Safety Team</h3>
                                    <div className="space-y-3">
                                        <a
                                            href="mailto:info@swagshots.com"
                                            className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                                        >
                                            info@swagshots.com
                                        </a>
                                        <p className="text-red-700 text-sm">Mark emails as "URGENT SAFETY" for priority handling</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Resources */}
                    <section className="text-center">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Additional Resources</h2>
                        <p className="text-gray-600 mb-12 max-w-4xl mx-auto text-lg leading-relaxed">
                            We partner with leading child safety organizations to provide additional resources and support.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            <Link to="/privacy-policy" className="group">
                                <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                    <div className="text-4xl mb-4">🔒</div>
                                    <h3 className="text-lg font-semibold text-primary-600 mb-2">Privacy Policy</h3>
                                    <p className="text-gray-600 text-sm">Learn how we protect your data</p>
                                </div>
                            </Link>
                            <Link to="/terms" className="group">
                                <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                    <div className="text-4xl mb-4">📋</div>
                                    <h3 className="text-lg font-semibold text-primary-600 mb-2">Terms & Conditions</h3>
                                    <p className="text-gray-600 text-sm">Understand our platform rules</p>
                                </div>
                            </Link>
                            <Link to="/contact" className="group">
                                <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                    <div className="text-4xl mb-4">💬</div>
                                    <h3 className="text-lg font-semibold text-primary-600 mb-2">Contact Support</h3>
                                    <p className="text-gray-600 text-sm">Get help when you need it</p>
                                </div>
                            </Link>
                        </div>
                    </section>
                </div>
            </div>
        </Layout>
    );
};
